import type { ReactNode } from 'react';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootReduxState } from 'app/shared/flux/reducers/index';
import type { Photo } from 'app/types/commonTypes.type';
import { buildResponsiveImageAttrs } from 'app/shared/utils/fedPhotos';
import { buildListingPhotoAlt } from 'app/shared/utils/listingUtils';
import { FilterPills } from './FilterPills';
import { PhotoLightbox } from './PhotoLightbox';
import * as styles from './mediaCenter.styles';

type PhotosTabProps = {
  photos: Array<Photo>;
  headerActions?: ReactNode;
};

const FILTER_PILLS = [
  { label: 'Apartment', active: false, disabled: true },
  { label: 'Building', active: true, disabled: false },
  { label: 'Neighborhood', active: false, disabled: true },
];

const enabledPills = FILTER_PILLS.filter((pill) => !pill.disabled);

export const PhotosTab = ({ photos, headerActions }: PhotosTabProps) => {
  const displayName = useSelector((state: RootReduxState) => state.currentListingDetails?.currentListing?.displayName);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const handlePhotoClick = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  return (
    <div role="tabpanel" id="tabpanel-photos" aria-labelledby="tab-photos">
      {enabledPills.length > 0 && <FilterPills pills={enabledPills} />}
      <div className={styles.photosGrid} tabIndex={0} role="region" aria-label="Property photos">
        {photos.map((photo, index) => {
          const imageAttrs = buildResponsiveImageAttrs(photo.url, 768, [384, 768, 1152]);

          return (
            <button
              key={index}
              className={styles.photoButton}
              onClick={() => handlePhotoClick(index)}
              aria-label={`View photo ${index + 1} of ${photos.length}`}
            >
              {imageAttrs ? (
                <img
                  src={imageAttrs.src}
                  srcSet={imageAttrs.srcSet}
                  sizes="100vw"
                  alt={buildListingPhotoAlt(displayName, photo.caption, index)}
                  className={styles.photoImage}
                  loading={index > 1 ? 'lazy' : undefined}
                />
              ) : (
                <img
                  src={photo.url}
                  alt={buildListingPhotoAlt(displayName, photo.caption, index)}
                  className={styles.photoImage}
                  loading={index > 1 ? 'lazy' : undefined}
                />
              )}
            </button>
          );
        })}
      </div>

      <PhotoLightbox
        photos={photos}
        initialIndex={lightboxIndex}
        open={lightboxOpen}
        onOpenChange={setLightboxOpen}
        headerActions={headerActions}
      />
    </div>
  );
};
